// src/pages/Producao.js — Painel Kanban de Produção
// Fonte: /api/producao/kanban (Firebird FC12100 + SQL Server REQUISICOES + controle_envio)
import React, { useState, useEffect, useCallback, useMemo, useDeferredValue, startTransition } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Chip, Stack,
  CircularProgress, Alert, IconButton, Tooltip, MenuItem, Select, FormControl,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableHead, TableRow, Divider
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon, Inventory2 as InventoryIcon,
  Storefront as StoreIcon, Science as LabIcon, Factory as FactoryIcon,
  CheckCircle as CheckIcon,
  WarningAmber as WarningIcon, PlayCircleOutline as PlayIcon,
  HourglassBottom as HourglassIcon, Close as CloseIcon, Clear as ClearIcon,
  Refresh as RefreshIcon, Search as SearchIcon,
  PendingActions as PendingActionsIcon,
  Medication as MedicationIcon
} from '@mui/icons-material';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useAuth } from '../contexts/AuthContext';

// Referência estável usada como fallback quando uma coluna está ausente em
// dadosPorColuna — evita criar `[]` novo a cada render (quebraria React.memo).
const EMPTY_ARRAY = Object.freeze([]);

// Altura do slot de cada card na lista virtualizada. 200px cobre o pior caso
// (badge AGUARD. EXPEDIÇÃO + SEM BIP CONFER. + destino + data + status pill).
// Cards menores ficam com leve sobra abaixo, aceitável.
const CARD_SLOT_HEIGHT = 200;

const COLUNAS_KEYS = ['incluidas', 'pendentes_lab', 'liberadas', 'falta_produzir', 'em_producao', 'produzidos', 'pendentes_exped', 'recebida_loja'];
// Estado inicial estável: todas colunas → EMPTY_ARRAY (mesma ref). Sem isso,
// cada render do componente criaria um objeto novo com [] novos, derrubando
// React.memo do KanbanColumn.
const EMPTY_DADOS = Object.freeze(
  COLUNAS_KEYS.reduce((acc, k) => { acc[k] = EMPTY_ARRAY; return acc; }, {})
);

const COLUNAS = [
  { key: 'incluidas',       titulo: 'Incluídas',     tituloLong: 'Fórmulas Incluídas',      icon: <InventoryIcon fontSize="small" />, headerBg: '#e0f2fe', headerColor: '#0369a1', accent: '#38bdf8' },
  { key: 'pendentes_lab',   titulo: 'Pend. Visto',   tituloLong: 'Pendente Visto',          icon: <WarningIcon  fontSize="small" />, headerBg: '#ffedd5', headerColor: '#c2410c', accent: '#f97316' },
  { key: 'liberadas',       titulo: 'Liberadas',     tituloLong: 'Liberadas para Produção', icon: <PlayIcon     fontSize="small" />, headerBg: '#e0f7fa', headerColor: '#0e7490', accent: '#22d3ee' },
  { key: 'falta_produzir',  titulo: 'Na Fila',       tituloLong: 'Na Fila',                 icon: <PendingActionsIcon fontSize="small" />, headerBg: '#ccfbf1', headerColor: '#0f766e', accent: '#14b8a6' },
  { key: 'em_producao',     titulo: 'Em Produção',   tituloLong: 'Em Produção',             icon: <FactoryIcon  fontSize="small" />, headerBg: '#fef9c3', headerColor: '#a16207', accent: '#facc15' },
  { key: 'produzidos',      titulo: 'Produzidos',    tituloLong: 'Produzidos',              icon: <CheckIcon    fontSize="small" />, headerBg: '#f3e8ff', headerColor: '#7e22ce', accent: '#c084fc' },
  { key: 'pendentes_exped', titulo: 'Pend. Exped.',  tituloLong: 'Pendentes Expedição',     icon: <HourglassIcon fontSize="small" />, headerBg: '#fef9c3', headerColor: '#a16207', accent: '#facc15' },
  { key: 'recebida_loja',   titulo: 'Receb. Loja',   tituloLong: 'Recebido Loja',           icon: <StoreIcon    fontSize="small" />, headerBg: '#dbeafe', headerColor: '#1d4ed8', accent: '#3b82f6' }
];

// De-para oficial dos códigos TPFORMAFARMA do ERP. Códigos vistos em FC12100
// que não estiverem aqui caem no fallback do número (ex.: "22").
const FORMA_FARMA_NOMES = {
  1: 'Cápsula',
  2: 'Creme',
  3: 'Loção',
  4: 'Shampoo',
  5: 'Outras',
  6: 'Unidades',
  7: 'Homeopatia',
  8: 'Floral',
  9: 'Comprimido',
  10: 'Gel',
  11: 'Pomada',
  12: 'Xarope',
  13: 'Filtro Solar',
  14: 'Injetável',
  15: 'Envelope',
  16: 'Biscoito Medicamentoso',
  17: 'Pastilha Medicamentosa',
  18: 'Patch Gel',
  19: 'Filmes',
  20: 'Pasta Oral',
  21: 'Solução Oral'
};

const STATUS_TONE = {
  info:    { bg: '#dbeafe', color: '#1d4ed8', icon: <LabIcon sx={{ fontSize: 14 }} /> },
  error:   { bg: '#fee2e2', color: '#b91c1c', icon: <CloseIcon sx={{ fontSize: 14 }} /> },
  warning: { bg: '#fef3c7', color: '#a16207', icon: <WarningIcon sx={{ fontSize: 14 }} /> },
  success: { bg: '#dcfce7', color: '#15803d', icon: <CheckIcon sx={{ fontSize: 14 }} /> }
};

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('pt-BR');
};
const formatDateTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('pt-BR');
};

// Combina DTRET (data) + HRRET (hora) num único timestamp para exibição.
// HRRET pode vir como:
//   - string "HH:MM:SS" (Firebird TIME serializado)
//   - ISO datetime com a parte de data zerada (1970-01-01THH:MM:SS)
//   - Date object — no driver atual, costuma ser ISO string
const formatRetirada = (dtret, hrret) => {
  if (!dtret) return null;
  const d = new Date(dtret);
  if (Number.isNaN(d.getTime())) return null;
  let hh = 0, mm = 0;
  if (hrret) {
    const s = String(hrret);
    const m = s.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (m) { hh = parseInt(m[1], 10); mm = parseInt(m[2], 10); }
  }
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const ano = d.getFullYear();
  return `${dia}/${mes}/${ano} ${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

// Decodifica strings duplo-encodadas em UTF-8 (Firebird WIN1252→UTF8 vai pra UTF8 de novo)
const fixEncoding = (s) => {
  if (!s) return s;
  try {
    return decodeURIComponent(escape(s));
  } catch (_) {
    return s;
  }
};

// Card visual a partir do payload do endpoint
const cardFromKanban = (it, statusOverride) => {
  const retirada = formatRetirada(it.dtret, it.hrret);
  const entrada = formatDate(it.dtentr);
  const cdfil = String(it.cdfil ?? '');
  const nrrqu = String(it.nrrqu ?? '');
  const serier = String(it.serier ?? '');
  return {
    codigo: `${it.cdfil}-${it.nrrqu}-${it.serier}`,
    filialCod: String(it.cdfile ?? '').padStart(3, '0'),
    filial: fixEncoding(it.filial_estoque_nome) || '',
    destinoCod: String(it.cdfild ?? '').padStart(3, '0'),
    destino: fixEncoding(it.filial_destino_nome) || '',
    serie: it.serier,
    qtd: 1,
    entrada,
    retirada,
    badge: statusOverride?.badge,
    status: statusOverride?.status,
    confInferida: !!it.conf_inferida,
    // chave de busca pré-calculada (evita rebuildar a string a cada tecla digitada)
    _searchKey: `${cdfil}${nrrqu}${serier}|${cdfil}-${nrrqu}-${serier}`.toLowerCase(),
    raw: it
  };
};

// Status visual exibido em cada coluna
const statusByColuna = (col, item) => {
  switch (col) {
    case 'pendentes_lab':
      return { status: { label: 'Não encontrado no LAB', tone: 'error' } };
    case 'liberadas':
      return { status: { label: 'Liberada (Farmacêutica)', when: formatDateTime(item.data_liberacao_farmac || item.ultima_data_lab), tone: 'info' } };
    case 'falta_produzir':
      return { status: { label: 'Aguardando produção', when: formatDateTime(item.data_liberacao_farmac), tone: 'warning' } };
    case 'em_producao':
      return { status: { label: fixEncoding(item.ultimo_setor_lab) || 'Em produção', when: formatDateTime(item.ultima_data_lab), tone: 'info' } };
    case 'produzidos':
      return { status: { label: 'Conferência', when: formatDateTime(item.data_conferencia), tone: 'success' } };
    case 'pendentes_exped':
      return { badge: 'AGUARD. EXPEDIÇÃO',
               status: item.ultimo_setor_lab ? { label: fixEncoding(item.ultimo_setor_lab), when: formatDateTime(item.ultima_data_lab), tone: 'info' } : null };
    case 'recebida_loja':
      return { status: { label: fixEncoding(item.ultimo_setor_lab) || 'Recebida na Loja',
                         when: formatDateTime(item.ultima_data_lab),
                         tone: 'success' } };
    case 'incluidas':
    default:
      return item.ultimo_setor_lab
        ? { status: { label: fixEncoding(item.ultimo_setor_lab), when: formatDateTime(item.ultima_data_lab), tone: 'info' } }
        : {};
  }
};

const FilialChip = React.memo(({ codigo, nome }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
    <Box sx={{
      bgcolor: '#f1f5f9', color: '#475569', fontWeight: 700,
      px: 0.75, py: 0.125, borderRadius: 0.75,
      fontSize: { xs: 12, md: 10.5, lg: 11.5 }, lineHeight: 1.5, flexShrink: 0
    }}>{codigo || '—'}</Box>
    {nome && (
      <Typography
        sx={{ fontSize: { xs: 12, md: 10.5, lg: 11.5 }, color: '#64748b', fontWeight: 500, minWidth: 0 }}
        noWrap
        title={nome}
      >
        {nome}
      </Typography>
    )}
  </Box>
));

const StatusPill = React.memo(({ status }) => {
  const tone = STATUS_TONE[status.tone] || STATUS_TONE.info;
  return (
    <Box sx={{
      mt: 1, p: { xs: 0.75, md: 0.5 }, borderRadius: 1,
      border: `1px dashed ${tone.color}55`, bgcolor: tone.bg,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, minWidth: 0
    }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.5,
        color: tone.color, fontWeight: 600,
        fontSize: { xs: 12, md: 10, lg: 11 },
        textAlign: 'center', minWidth: 0
      }}>
        {tone.icon}<span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{status.label}</span>
      </Box>
      {status.when && (
        <Typography sx={{ fontSize: { xs: 10.5, md: 9, lg: 10 }, color: tone.color, opacity: 0.85 }}>
          ({status.when})
        </Typography>
      )}
    </Box>
  );
});

const RequisicaoCard = React.memo(({ item, accent, onOpen }) => (
  <Paper
    elevation={0}
    onClick={() => onOpen?.(item)}
    role="button"
    aria-label={`Abrir fórmula da requisição ${item.codigo}`}
    sx={{
      p: { xs: 1.25, md: 0.75, lg: 1 }, mb: 0.75, borderRadius: 1.5,
      border: '1px solid #e2e8f0', borderLeft: `3px solid ${accent}`,
      bgcolor: '#fff', transition: 'all .15s ease', minWidth: 0,
      cursor: 'pointer',
      '&:hover': { boxShadow: '0 2px 6px rgba(0,0,0,.08)', transform: 'translateY(-1px)' }
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, gap: 0.5, minWidth: 0 }}>
      <Typography
        sx={{
          fontFamily: 'monospace', fontWeight: 700,
          fontSize: { xs: 13, md: 11.5, lg: 12.5 }, color: '#0f172a',
          minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
        }}
        title={item.codigo}
      >
        {item.codigo}
      </Typography>
      <Box sx={{ width: 4, height: 14, bgcolor: '#cbd5e1', borderRadius: 0.5, flexShrink: 0 }} />
    </Box>

    {item.badge && (
      <Chip
        size="small"
        icon={<CloseIcon sx={{ fontSize: 12 }} />}
        label={item.badge}
        sx={{
          height: { xs: 18, md: 16 }, fontSize: { xs: 10, md: 9 }, fontWeight: 700, mb: 0.5,
          bgcolor: '#fee2e2', color: '#b91c1c',
          '& .MuiChip-icon': { color: '#b91c1c', ml: 0.5 },
          '& .MuiChip-label': { px: 0.6 }
        }}
      />
    )}

    {item.confInferida && (
      <Chip
        size="small"
        icon={<WarningIcon sx={{ fontSize: 12 }} />}
        label="SEM BIP CONFER."
        title="Foi para a loja sem registro de Conferência/Liberação"
        sx={{
          height: { xs: 18, md: 16 }, fontSize: { xs: 10, md: 9 }, fontWeight: 700, mb: 0.5,
          bgcolor: '#fee2e2', color: '#b91c1c',
          '& .MuiChip-icon': { color: '#b91c1c', ml: 0.5 },
          '& .MuiChip-label': { px: 0.6 }
        }}
      />
    )}

    <FilialChip codigo={item.filialCod} nome={item.filial} />

    <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 1 }, mt: 0.5, flexWrap: 'wrap' }}>
      <Typography sx={{ fontSize: { xs: 12, md: 10.5, lg: 11.5 }, color: '#64748b' }}>
        Série: <strong style={{ color: '#0f172a' }}>{item.serie}</strong>
      </Typography>
      <Typography sx={{ fontSize: { xs: 12, md: 10.5, lg: 11.5 }, color: '#64748b' }}>
        Qtd: <strong style={{ color: '#0f172a' }}>{item.qtd}</strong>
      </Typography>
    </Box>

    {item.destino && (
      <Typography sx={{ fontSize: { xs: 11.5, md: 10, lg: 11 }, color: '#64748b', mt: 0.25 }} noWrap title={item.destino}>
        → <strong style={{ color: '#0f172a' }}>{item.destinoCod}</strong> {item.destino}
      </Typography>
    )}

    {item.entrada && (
      <Typography sx={{ fontSize: { xs: 12, md: 10.5, lg: 11.5 }, color: '#64748b', mt: 0.25 }}>
        Entrada: <strong style={{ color: '#0f172a' }}>{item.entrada}</strong>
      </Typography>
    )}
    {item.retirada && (
      <Typography sx={{ fontSize: { xs: 12, md: 10.5, lg: 11.5 }, color: '#64748b' }}>
        Retirada: <strong style={{ color: '#0f172a' }}>{item.retirada}</strong>
      </Typography>
    )}

    {item.status && <StatusPill status={item.status} />}
  </Paper>
));

// ─── Modal de fórmula ───────────────────────────────────────────────────────
// Abre no CLIQUE do card (não no hover, de propósito): cada exibição custa
// uma ida ao Firebird (FC12100 + FC12110) pelo pool de 10 conexões, e hover
// ao percorrer uma coluna dispararia dezenas de queries. Clique = 1 query.
const CampoFicha = ({ rotulo, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
      {rotulo}
    </Typography>
    <Typography sx={{ fontSize: 13.5, color: '#0f172a', fontWeight: 500, wordBreak: 'break-word' }}>
      {(children ?? '') === '' ? '—' : children}
    </Typography>
  </Box>
);

const FichaDialog = ({ card, api, onClose }) => {
  const [estado, setEstado] = useState({ loading: true, erro: null, dados: null });

  const buscar = useCallback(() => {
    if (!card) return;
    setEstado({ loading: true, erro: null, dados: null });
    const raw = card.raw || {};
    // SERIER vem cru da FC12100 (CHAR); '0' cobre o raro caso de vir vazio.
    api.fichaProducao(raw.cdfil, raw.nrrqu, String(raw.serier ?? '').trim() || '0')
      .then(resp => setEstado({ loading: false, erro: null, dados: resp?.dados || null }))
      .catch(e => setEstado({ loading: false, erro: e?.message || 'Erro ao buscar a ficha', dados: null }));
  }, [card, api]);

  useEffect(() => { buscar(); }, [buscar]);

  const d = estado.dados;
  const ehCapsula = d?.unidade_volume === 'CAP';

  return (
    <Dialog open={!!card} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <MedicationIcon sx={{ color: '#6366f1' }} />
        <Typography component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 18 }}>
          {card?.codigo}
        </Typography>
        {d?.forma_nome && (
          <Chip size="small" label={d.forma_nome}
                sx={{ fontWeight: 700, bgcolor: '#eef2ff', color: '#4338ca' }} />
        )}
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose} aria-label="Fechar"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {estado.loading && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress size={28} />
            <Typography sx={{ mt: 1, color: '#64748b', fontSize: 13 }}>Buscando ficha no FormulaCerta…</Typography>
          </Box>
        )}

        {estado.erro && (
          <Alert severity="error" action={
            <Button color="inherit" size="small" onClick={buscar}>Tentar novamente</Button>
          }>
            {estado.erro}
          </Alert>
        )}

        {d && (
          <>
            <Box sx={{
              display: 'grid', gap: 1.5, mb: 2,
              gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }
            }}>
              <CampoFicha rotulo="Paciente">{d.paciente}</CampoFicha>
              <CampoFicha rotulo="Forma farmacêutica">{d.forma_nome}</CampoFicha>
              <CampoFicha rotulo="Quantidade">
                {d.quantidade_calculada != null
                  ? `${d.quantidade_calculada} ${d.unidade_volume || ''}`.trim()
                  : null}
              </CampoFicha>
              {ehCapsula && (
                <CampoFicha rotulo="Cápsula (tipo / frascos / por frasco)">
                  {[d.tipo_capsula, d.qtfor, d.qtcont].filter(v => v != null && v !== '').join(' / ')}
                </CampoFicha>
              )}
              <CampoFicha rotulo="Entrada">{formatDate(d.data_entrada)}</CampoFicha>
              <CampoFicha rotulo="Retirada">
                {[formatDate(d.data_retirada), d.hora_retirada].filter(Boolean).join(' ')}
              </CampoFicha>
              <CampoFicha rotulo="Validade">{formatDate(d.data_validade)}</CampoFicha>
              <CampoFicha rotulo="Estoque">
                {d.filial_estoque?.nome
                  ? `${String(d.filial_estoque.codigo).padStart(3, '0')} ${d.filial_estoque.nome}` : null}
              </CampoFicha>
              <CampoFicha rotulo="Destino">
                {d.filial_destino?.nome
                  ? `${String(d.filial_destino.codigo).padStart(3, '0')} ${d.filial_destino.nome}` : null}
              </CampoFicha>
            </Box>

            {d.posologia && (
              <Box sx={{ mb: 1.5 }}>
                <CampoFicha rotulo="Posologia">{d.posologia}</CampoFicha>
              </Box>
            )}
            {d.observacoes && (
              <Box sx={{ mb: 1.5 }}>
                <CampoFicha rotulo="Observações">{d.observacoes}</CampoFicha>
              </Box>
            )}

            <Divider sx={{ my: 1.5 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 13.5, mb: 0.75, color: '#334155' }}>
              Componentes ({d.itens?.length || 0})
            </Typography>

            {(d.itens?.length || 0) === 0 ? (
              <Typography sx={{ color: '#94a3b8', fontSize: 13 }}>
                Nenhum componente encontrado para esta ficha.
              </Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Componente</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Qtd</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Unid.</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Pesado</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Lote</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {d.itens.map(i => (
                      <TableRow key={i.itemid} hover>
                        <TableCell sx={{ fontSize: 12.5 }}>{i.descricao || i.cdpro}</TableCell>
                        <TableCell sx={{ fontSize: 12.5 }} align="right">{i.quantidade ?? '—'}</TableCell>
                        <TableCell sx={{ fontSize: 12.5 }}>{i.unidade || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 12.5 }} align="right">{i.qt_pesada ?? '—'}</TableCell>
                        <TableCell sx={{ fontSize: 12.5, fontFamily: 'monospace' }}>{i.lote || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

const ColunaVazia = React.memo(({ titulo }) => (
  <Box sx={{
    p: 2, textAlign: 'center', color: '#94a3b8', bgcolor: '#f8fafc',
    border: '1px dashed #cbd5e1', borderRadius: 1.5, fontSize: 12.5
  }}>
    📭 Nenhuma requisição {titulo.toLowerCase()}
  </Box>
));

// Linha da lista virtualizada — só monta cards visíveis no viewport. Trocar
// filtros que reduzem 1000+ → 3 cards deixa de pagar unmount de centenas
// de Papers, que era o gargalo do INP.
const VirtualRow = React.memo(({ index, style, data }) => {
  const item = data.items[index];
  return (
    <div style={style}>
      <RequisicaoCard item={item} accent={data.accent} onOpen={data.onOpen} />
    </div>
  );
}, (prev, next) =>
  prev.index === next.index &&
  prev.data === next.data &&
  prev.style.top === next.style.top
);

// Coluna do Kanban — memoizada. Re-renderiza só quando 'itens' (ou loading)
// mudam de referência. Como 'itens' vêm de useMemo em dadosPorColuna, mudanças
// em estado não relacionado (data, filiais, foco em input) NÃO disparam
// reconciliation dos ~1000 cards filhos.
const KanbanColumn = React.memo(({ col, itens, loading, onOpen }) => {
  // onOpen entra no itemData (e não via context) pra manter o comparator do
  // VirtualRow (prev.data === next.data) funcionando: o callback é estável
  // (useCallback no pai), então a referência de itemData só muda com itens.
  const itemData = useMemo(() => ({ items: itens, accent: col.accent, onOpen }), [itens, col.accent, onOpen]);
  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0',
        borderTop: `3px solid ${col.accent}`, display: 'flex', flexDirection: 'column',
        height: 'calc(100vh - 180px)', minWidth: 0
      }}
    >
      <Box sx={{
        px: { xs: 1, md: 0.5, lg: 0.75 },
        py: { xs: 0.75, md: 0.5 },
        borderBottom: '1px solid #e2e8f0',
        bgcolor: col.headerBg, color: col.headerColor,
        borderTopLeftRadius: 8, borderTopRightRadius: 8,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 0.5,
        flexShrink: 0
      }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: { xs: 'flex', md: 'none', xl: 'flex' }, alignItems: 'center', flexShrink: 0 }}>
            {col.icon}
          </Box>
          <Typography
            sx={{
              fontSize: { xs: 12.5, sm: 11, md: 10.5, lg: 11.5, xl: 12.5 },
              fontWeight: 700, lineHeight: 1.2, minWidth: 0
            }}
            noWrap
            title={col.tituloLong}
          >
            {col.titulo}
          </Typography>
        </Stack>
        <Chip
          label={itens.length}
          size="small"
          sx={{
            height: { xs: 20, md: 16, lg: 18 },
            fontWeight: 700,
            fontSize: { xs: 11, md: 9.5, lg: 10.5 },
            bgcolor: '#fff', color: col.headerColor,
            border: `1px solid ${col.headerColor}33`,
            flexShrink: 0,
            '& .MuiChip-label': { px: 0.6 }
          }}
        />
      </Box>
      {loading ? (
        <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
      ) : itens.length === 0 ? (
        <Box sx={{ p: { xs: 1, md: 0.5, lg: 0.75 } }}>
          <ColunaVazia titulo={col.tituloLong} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minWidth: 0, px: { xs: 1, md: 0.5, lg: 0.75 }, pt: { xs: 1, md: 0.5, lg: 0.75 } }}>
          <AutoSizer>
            {({ height, width }) => (
              <FixedSizeList
                height={height}
                width={width}
                itemCount={itens.length}
                itemSize={CARD_SLOT_HEIGHT}
                itemData={itemData}
                overscanCount={3}
              >
                {VirtualRow}
              </FixedSizeList>
            )}
          </AutoSizer>
        </Box>
      )}
    </Paper>
  );
});

// Input de busca isolado: mantém o valor digitado em estado local pra que
// teclar não force re-render do <Producao> inteiro (que incluiria 8 FiltroCampo,
// 3 Selects com filiais.map, etc.). Notifica o pai dentro de startTransition,
// pra que o setState que dispara a filtragem dos cards seja interruptível.
// Reset via prop `resetKey` — bumpá-la força remount e zera o estado local.
const SearchInput = React.memo(({ onChange }) => {
  const [local, setLocal] = useState('');
  return (
    <TextField
      size="small" variant="standard"
      value={local}
      onChange={(e) => {
        const v = e.target.value;
        setLocal(v);
        startTransition(() => onChange(v));
      }}
      placeholder="código ou nº requisição"
      InputProps={{ disableUnderline: true, sx: { fontSize: 12.5, fontWeight: 600 } }}
      fullWidth
    />
  );
});

// Caixa de filtro pílula com input controlado (compacta)
const FiltroCampo = ({ icon, label, children, dimmed, minWidth = 170 }) => (
  <Paper elevation={0} sx={{
    px: 1, py: 0.25, borderRadius: 999, border: '1px solid #e2e8f0',
    display: 'flex', alignItems: 'center', gap: 0.75, minWidth, bgcolor: dimmed ? '#f1f5f9' : '#fff',
    opacity: dimmed ? 0.6 : 1
  }}>
    <Box sx={{ color: '#64748b', display: 'flex' }}>{icon}</Box>
    <Typography sx={{ fontSize: 11.5, color: '#475569', whiteSpace: 'nowrap' }}>{label}:</Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Paper>
);

const hojeISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const Producao = () => {
  const { api } = useAuth();

  // Datas: XOR — uma trava a outra
  const [dataEntrada, setDataEntrada]   = useState(hojeISO());
  const [dataRetirada, setDataRetirada] = useState('');

  // Hora de retirada: só faz sentido junto com Data de Retirada
  const [horaInicio, setHoraInicio] = useState('');
  const [horaFim, setHoraFim]       = useState('');

  const [filialEstoque, setFilialEstoque] = useState('');
  const [filialDestino, setFilialDestino] = useState('');
  const [filialOrigem, setFilialOrigem] = useState('');
  const [formaFarma, setFormaFarma] = useState('');
  const [statusLab, setStatusLab] = useState('');
  const [busca, setBusca] = useState('');
  // Bumpado pelo Limpar pra remontar SearchInput (que mantém estado local
  // do que está digitado, pra evitar re-render do pai a cada tecla).
  const [buscaResetKey, setBuscaResetKey] = useState(0);

  const [filiais, setFiliais] = useState([]);
  const [formasFarma, setFormasFarma] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [colunasApi, setColunasApi] = useState(null);

  // Card aberto no modal de fórmula. Callbacks estáveis: entram no itemData
  // das colunas memoizadas e não podem mudar de referência a cada render.
  const [fichaCard, setFichaCard] = useState(null);
  const abrirFicha  = useCallback((item) => setFichaCard(item), []);
  const fecharFicha = useCallback(() => setFichaCard(null), []);

  // Carrega filiais para os dropdowns
  useEffect(() => {
    api.listarFiliais()
      .then(resp => setFiliais(resp?.dados || resp?.data || []))
      .catch(() => {});
  }, [api]);

  // Carrega lista de formas farmacêuticas (DISTINCT em FC12100) para o dropdown
  useEffect(() => {
    api.listarFormasFarma()
      .then(resp => setFormasFarma(resp?.dados || []))
      .catch(() => {});
  }, [api]);

  const carregar = useCallback(async () => {
    if (!dataEntrada && !dataRetirada) {
      setError('Informe Data de Entrada OU Data de Retirada.');
      setColunasApi(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const filtros = {};
      if (dataEntrada)   filtros.dtentr = dataEntrada;
      else               filtros.dtret  = dataRetirada;
      if (filialEstoque) filtros.cdfile = filialEstoque;
      if (filialDestino) filtros.cdfild = filialDestino;
      if (filialOrigem)  filtros.cdfilo = filialOrigem;
      if (formaFarma)    filtros.tpformafarma = formaFarma;
      // Hora só vale com Data de Retirada
      if (dataRetirada && horaInicio) filtros.hrret_inicio = horaInicio;
      if (dataRetirada && horaFim)    filtros.hrret_fim    = horaFim;

      const resp = await api.kanbanProducao(filtros);
      if (resp?.success) {
        // setColunasApi dispara cardsPorColuna + dadosPorColuna + reconciliation
        // de 2000+ cards. Em transition pra não bloquear o paint do click do
        // Aplicar/Atualizar, evitando INP de ~1s.
        startTransition(() => setColunasApi(resp.colunas || null));
      } else {
        setError(resp?.message || 'Erro ao carregar Kanban');
        setColunasApi(null);
      }
    } catch (e) {
      setError(e.message || 'Falha de conexão com a API');
      setColunasApi(null);
    } finally {
      setLoading(false);
    }
  }, [api, dataEntrada, dataRetirada, filialEstoque, filialDestino, filialOrigem, formaFarma, horaInicio, horaFim]);

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, []);

  const limpar = () => {
    setDataEntrada(hojeISO());
    setDataRetirada('');
    setHoraInicio('');
    setHoraFim('');
    setFilialEstoque('');
    setFilialDestino('');
    setFilialOrigem('');
    setFormaFarma('');
    setStatusLab('');
    setBusca('');
    setBuscaResetKey(k => k + 1);
  };

  const handleEntradaChange = (v) => {
    setDataEntrada(v);
    if (v) {
      setDataRetirada(''); // XOR
      setHoraInicio('');   // hora só vale com retirada
      setHoraFim('');
    }
  };
  const handleRetiradaChange = (v) => {
    setDataRetirada(v);
    if (v) setDataEntrada(''); // XOR
  };
  // XOR no foco: clicar/tabular no outro campo zera o atual
  const handleEntradaFocus = () => {
    if (dataRetirada) setDataRetirada('');
    if (horaInicio) setHoraInicio('');
    if (horaFim) setHoraFim('');
  };
  const handleRetiradaFocus = () => {
    if (dataEntrada) setDataEntrada('');
  };

  // Etapa cara — só refaz quando os dados da API mudam (não a cada tecla).
  const cardsPorColuna = useMemo(() => {
    if (!colunasApi) return null;
    const out = {};
    for (const col of COLUNAS) {
      out[col.key] = (colunasApi[col.key] || [])
        .map(it => cardFromKanban(it, statusByColuna(col.key, it)));
    }
    return out;
  }, [colunasApi]);

  // useDeferredValue mantém o UI responsivo: a filtragem fica em background
  // e o React mostra o resultado anterior enquanto recalcula. Sem o defer no
  // statusLab, clicar num MenuItem trava ~1.5s pra reconciliar 1000+ cards.
  const buscaDeferred = useDeferredValue(busca);
  const statusLabDeferred = useDeferredValue(statusLab);

  const dadosPorColuna = useMemo(() => {
    if (!cardsPorColuna) return EMPTY_DADOS;
    const termo = buscaDeferred.trim().toLowerCase();
    if (!termo && !statusLabDeferred) return cardsPorColuna;
    const out = {};
    for (const col of COLUNAS) {
      const src = cardsPorColuna[col.key] || EMPTY_ARRAY;
      if (src.length === 0) {
        out[col.key] = EMPTY_ARRAY;
        continue;
      }
      const filtered = src.filter(c => {
        if (statusLabDeferred && c.raw.setor_lab !== statusLabDeferred) return false;
        if (termo && !c._searchKey.includes(termo)) return false;
        return true;
      });
      // Se o filtro não rejeitou nada, devolve a ref original — KanbanColumn
      // memoizada bail-outa e nem reconcilia. Se zerou, devolve EMPTY_ARRAY
      // estável pra mesmo benefício em filtros que esvaziam colunas.
      if (filtered.length === src.length) out[col.key] = src;
      else if (filtered.length === 0) out[col.key] = EMPTY_ARRAY;
      else out[col.key] = filtered;
    }
    return out;
  }, [cardsPorColuna, buscaDeferred, statusLabDeferred]);

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', p: 2 }}>
      {/* Filtros topo */}
      <Paper elevation={0} sx={{
        p: 1, mb: 2, borderRadius: 2, border: '1px solid #e2e8f0',
        display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'center'
      }}>
        <FiltroCampo
          icon={<SearchIcon fontSize="small" />}
          label="Buscar"
          minWidth={180}
        >
          <SearchInput key={buscaResetKey} onChange={setBusca} />
        </FiltroCampo>

        <FiltroCampo
          icon={<CalendarIcon fontSize="small" />}
          label="Entrada"
          dimmed={!!dataRetirada}
          minWidth={155}
        >
          <TextField
            type="date" size="small" variant="standard"
            value={dataEntrada}
            onFocus={handleEntradaFocus}
            onChange={(e) => handleEntradaChange(e.target.value)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, fontWeight: 600 } }}
            fullWidth
          />
        </FiltroCampo>

        <FiltroCampo
          icon={<CalendarIcon fontSize="small" />}
          label="Retirada"
          dimmed={!!dataEntrada}
          minWidth={155}
        >
          <TextField
            type="date" size="small" variant="standard"
            value={dataRetirada}
            onFocus={handleRetiradaFocus}
            onChange={(e) => handleRetiradaChange(e.target.value)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, fontWeight: 600 } }}
            fullWidth
          />
        </FiltroCampo>

        <FiltroCampo
          icon={<CalendarIcon fontSize="small" />}
          label="Hora ini"
          dimmed={!dataRetirada}
          minWidth={120}
        >
          <TextField
            type="time" size="small" variant="standard"
            disabled={!dataRetirada}
            value={horaInicio}
            onChange={(e) => setHoraInicio(e.target.value)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, fontWeight: 600 } }}
            fullWidth
          />
        </FiltroCampo>

        <FiltroCampo
          icon={<CalendarIcon fontSize="small" />}
          label="Hora fim"
          dimmed={!dataRetirada}
          minWidth={120}
        >
          <TextField
            type="time" size="small" variant="standard"
            disabled={!dataRetirada}
            value={horaFim}
            onChange={(e) => setHoraFim(e.target.value)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, fontWeight: 600 } }}
            fullWidth
          />
        </FiltroCampo>

        <FiltroCampo icon={<StoreIcon fontSize="small" />} label="Origem" minWidth={170}>
          <FormControl size="small" variant="standard" fullWidth>
            <Select
              value={filialOrigem}
              onChange={(e) => setFilialOrigem(e.target.value)}
              disableUnderline
              displayEmpty
              sx={{ fontSize: 13, fontWeight: 600 }}
            >
              <MenuItem value=""><em>Todas</em></MenuItem>
              {filiais.map(f => (
                <MenuItem key={`fo-${f.codigo_filial}`} value={f.codigo_filial}>
                  {String(f.codigo_filial).padStart(3, '0')} {f.nome_filial}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FiltroCampo>

        <FiltroCampo icon={<StoreIcon fontSize="small" />} label="Estoque" minWidth={170}>
          <FormControl size="small" variant="standard" fullWidth>
            <Select
              value={filialEstoque}
              onChange={(e) => setFilialEstoque(e.target.value)}
              disableUnderline
              displayEmpty
              sx={{ fontSize: 13, fontWeight: 600 }}
            >
              <MenuItem value=""><em>Todas</em></MenuItem>
              {filiais.map(f => (
                <MenuItem key={`fe-${f.codigo_filial}`} value={f.codigo_filial}>
                  {String(f.codigo_filial).padStart(3, '0')} {f.nome_filial}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FiltroCampo>

        <FiltroCampo icon={<InventoryIcon fontSize="small" />} label="Destino" minWidth={170}>
          <FormControl size="small" variant="standard" fullWidth>
            <Select
              value={filialDestino}
              onChange={(e) => setFilialDestino(e.target.value)}
              disableUnderline
              displayEmpty
              sx={{ fontSize: 13, fontWeight: 600 }}
            >
              <MenuItem value=""><em>Todas</em></MenuItem>
              {filiais.map(f => (
                <MenuItem key={`fd-${f.codigo_filial}`} value={f.codigo_filial}>
                  {String(f.codigo_filial).padStart(3, '0')} {f.nome_filial}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FiltroCampo>

        <FiltroCampo icon={<MedicationIcon fontSize="small" />} label="Forma" minWidth={200}>
          <FormControl size="small" variant="standard" fullWidth>
            <Select
              value={formaFarma}
              onChange={(e) => setFormaFarma(e.target.value)}
              disableUnderline
              displayEmpty
              sx={{ fontSize: 13, fontWeight: 600 }}
            >
              <MenuItem value=""><em>Todas</em></MenuItem>
              {formasFarma.map(ff => (
                <MenuItem key={`ff-${ff.codigo}`} value={ff.codigo}>
                  {String(ff.codigo).padStart(2, '0')}
                  {FORMA_FARMA_NOMES[ff.codigo] ? ` ${FORMA_FARMA_NOMES[ff.codigo]}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FiltroCampo>

        <FiltroCampo icon={<LabIcon fontSize="small" />} label="Status" minWidth={170}>
          <FormControl size="small" variant="standard" fullWidth>
            <Select
              value={statusLab}
              onChange={(e) => {
                const val = e.target.value;
                // startTransition marca o update como low-priority: o menu
                // fecha imediato e a filtragem dos 1000+ cards roda depois,
                // sem bloquear o paint do click (INP).
                startTransition(() => setStatusLab(val));
              }}
              disableUnderline
              displayEmpty
              sx={{ fontSize: 13, fontWeight: 600 }}
            >
              <MenuItem value=""><em>Todos</em></MenuItem>
              <MenuItem value="visto_farmac">Visto Farmacêutico</MenuItem>
              <MenuItem value="ordem_prod">Ordem de Produção</MenuItem>
              <MenuItem value="pesagem">Pesagem</MenuItem>
              <MenuItem value="tritura">Trituração</MenuItem>
              <MenuItem value="encap">Encapsulação</MenuItem>
              <MenuItem value="peso_medio">Peso Médio</MenuItem>
              <MenuItem value="sache">Sachê</MenuItem>
              <MenuItem value="dermato">Dermato</MenuItem>
              <MenuItem value="xarope">Xarope</MenuItem>
              <MenuItem value="homeopat">Homeopatia</MenuItem>
              <MenuItem value="cosmet">Cosmético</MenuItem>
              <MenuItem value="conferenc">Conferência / Liberação</MenuItem>
              <MenuItem value="outro">Outro</MenuItem>
            </Select>
          </FormControl>
        </FiltroCampo>

        <Button
          variant="contained" color="primary" size="small"
          onClick={carregar} disabled={loading}
          sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 600, px: 1.5, fontSize: 12.5 }}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : 'Aplicar'}
        </Button>
        <Button
          variant="contained" color="error" size="small" startIcon={<ClearIcon />}
          sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 600, px: 1.5, fontSize: 12.5 }}
          onClick={limpar}
        >
          Limpar
        </Button>
        <Tooltip title="Atualizar">
          <span>
            <IconButton size="small" onClick={carregar} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Quadro Kanban */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(8, minmax(180px, 1fr))',
          sm: 'repeat(8, minmax(160px, 1fr))',
          md: 'repeat(8, minmax(0, 1fr))'
        },
        gap: { xs: 0.75, md: 0.5, lg: 0.75 },
        overflowX: { xs: 'auto', md: 'visible' },
        pb: 1
      }}>
        {COLUNAS.map(col => (
          <KanbanColumn
            key={col.key}
            col={col}
            itens={dadosPorColuna[col.key] || EMPTY_ARRAY}
            loading={loading}
            onOpen={abrirFicha}
          />
        ))}
      </Box>

      {/* Modal de fórmula — montado só com card aberto pra não pagar o
          Dialog no render dos 1000+ cards. */}
      {fichaCard && <FichaDialog card={fichaCard} api={api} onClose={fecharFicha} />}
    </Box>
  );
};

export default Producao;
